import { useRef, useState } from 'react';
import clsx from 'clsx';

// Knob geometry (px). Travel = trackWidth − KNOB − 2·EDGE_PAD.
const KNOB = 30;
const EDGE_PAD = 3;
// Fraction of the travel the knob must cross before a release commits.
// High enough that a stray tap (≈0 travel) never flips the state.
const COMMIT = 0.72;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Slide-to-confirm control — a drag-to-flip replacement for a tap
 * toggle where an accidental tap is costly (enabling / disabling the
 * trading bot). The user must drag the knob most of the way across the
 * track; a short drag or a stray tap springs back with no change.
 *
 * Keyboard users keep an Enter/Space shortcut (the accidental-tap
 * vector is touch, not the keyboard) so the control stays accessible.
 */
export function SlideToConfirm({
  on,
  onConfirm,
  disabled = false,
  busy = false,
  labelOff,
  labelOn,
  ariaLabel,
  className,
}: {
  on: boolean;
  onConfirm: (next: boolean) => void;
  disabled?: boolean;
  busy?: boolean;
  /** Hint shown while OFF — drag right to enable. */
  labelOff: string;
  /** Hint shown while ON — drag left to disable. */
  labelOn: string;
  ariaLabel?: string;
  className?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const travelRef = useRef(1);
  const startXRef = useRef(0);
  // null when at rest; a 0..1 fraction while dragging. Mirrored in a ref
  // so the pointerup handler reads the freshest value without a
  // side-effect inside a state updater (StrictMode double-invokes those).
  const dragRef = useRef<number | null>(null);
  const [drag, setDrag] = useState<number | null>(null);

  const locked = disabled || busy;
  const rest = on ? 1 : 0;
  const pos = drag ?? rest;
  // Committed once dragged ≥ COMMIT of the way toward the opposite end.
  const committed = on ? pos <= 1 - COMMIT : pos >= COMMIT;

  const setDragPos = (v: number | null) => {
    dragRef.current = v;
    setDrag(v);
  };
  const begin = (clientX: number, el: HTMLElement, pointerId: number) => {
    if (locked) return;
    const track = trackRef.current;
    if (!track) return;
    travelRef.current = Math.max(1, track.clientWidth - KNOB - EDGE_PAD * 2);
    startXRef.current = clientX;
    setDragPos(rest);
    el.setPointerCapture?.(pointerId);
  };
  const move = (clientX: number) => {
    if (dragRef.current === null) return;
    const dx = (clientX - startXRef.current) / travelRef.current;
    setDragPos(clamp(rest + dx, 0, 1));
  };
  const end = () => {
    const cur = dragRef.current;
    if (cur === null) return;
    setDragPos(null);
    const done = on ? cur <= 1 - COMMIT : cur >= COMMIT;
    if (done) onConfirm(!on);
  };

  const knobLeft = `calc(${EDGE_PAD}px + ${pos} * (100% - ${KNOB}px - ${EDGE_PAD * 2}px))`;

  return (
    <div
      ref={trackRef}
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      aria-disabled={locked || undefined}
      tabIndex={locked ? -1 : 0}
      onKeyDown={(e) => {
        if (locked) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onConfirm(!on);
        }
      }}
      className={clsx(
        'pb-focus-ring relative flex h-[38px] w-full select-none items-center overflow-hidden rounded-full transition-colors',
        on ? 'bg-pb-accent/15' : 'bg-pb-paper2',
        locked && 'opacity-60',
        className,
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center gap-1.5 px-9 text-[12px] font-medium text-tg-hint"
      >
        {on ? (
          <>
            <ArrowLeftGlyph />
            <span className="truncate">{labelOn}</span>
          </>
        ) : (
          <>
            <span className="truncate">{labelOff}</span>
            <ArrowRightGlyph />
          </>
        )}
      </span>
      <span
        role="presentation"
        onPointerDown={(e) => begin(e.clientX, e.currentTarget, e.pointerId)}
        onPointerMove={(e) => move(e.clientX)}
        onPointerUp={end}
        onPointerCancel={end}
        className={clsx(
          'absolute top-1/2 z-10 flex h-[32px] w-[30px] -translate-y-1/2 touch-none items-center justify-center rounded-full shadow-sm transition-colors',
          committed ? 'bg-pb-accent text-white' : 'bg-white text-tg-hint',
          !locked && 'cursor-grab active:cursor-grabbing',
          // Spring back / settle only when not actively dragging.
          drag === null && 'transition-[left,background-color]',
        )}
        style={{ left: knobLeft }}
      >
        <GripGlyph />
      </span>
    </div>
  );
}

function ArrowRightGlyph() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowLeftGlyph() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M19 12H5M11 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GripGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 6v12M15 6v12"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
      />
    </svg>
  );
}
